import firebase from "firebase/compat/app";
import { GetDB, generateGUID } from "./database/firebase-budget-tool";

/**
 * Iframe Error Logger Utility
 * 
 * This utility captures and logs errors from iframes according to the ERROR_LOGGING_STANDARD.md
 * Handles both same-origin and cross-origin iframe scenarios.
 */

class IframeErrorLogger {
    constructor() {
        this.sessionId = generateGUID();
        this.iframeErrorListeners = new Map();
        this.setupGlobalErrorListener();
        this.setupPostMessageListener();
    }

    /**
     * Initialize error logging for a specific iframe
     * @param {HTMLIFrameElement} iframe - The iframe element to monitor
     * @param {string} applicationName - The application identifier for the iframe
     */
    initializeIframeErrorLogging(iframe, applicationName = 'budget-calculator') {
        if (!iframe || !iframe.contentWindow) {
            console.warn('Invalid iframe provided to error logger');
            return;
        }

        const iframeId = iframe.id || `iframe-${Date.now()}`;
        
        // Store iframe info for later use
        this.iframeErrorListeners.set(iframeId, {
            iframe,
            applicationName,
            iframeSrc: iframe.src
        });

        // Set iframe ID if not present
        if (!iframe.id) {
            iframe.id = iframeId;
        }

        // Try to inject error handling script for same-origin iframes
        this.tryInjectErrorHandling(iframe, applicationName);

        // Setup iframe load/error listeners
        this.setupIframeListeners(iframe, applicationName);

        console.log(`Iframe error logging initialized for: ${applicationName}`);
    }

    /**
     * Try to inject error handling into same-origin iframes
     */
    tryInjectErrorHandling(iframe, applicationName) {
        try {
            iframe.onload = () => {
                try {
                    const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
                    
                    if (iframeDocument) {
                        // Same-origin iframe - we can directly inject error handling
                        this.injectErrorScript(iframeDocument, applicationName);
                    }
                } catch (crossOriginError) {
                    // Cross-origin iframe - use postMessage approach
                    console.log('Cross-origin iframe detected, using postMessage approach');
                    this.requestErrorReporting(iframe);
                }
            };
        } catch (error) {
            console.warn('Could not setup iframe error injection:', error);
        }
    }

    /**
     * Inject error handling script into same-origin iframe
     */
    injectErrorScript(iframeDocument, applicationName) {
        const script = iframeDocument.createElement('script');
        script.textContent = `
            (function() {
                // Capture JavaScript errors
                window.addEventListener('error', function(event) {
                    window.parent.postMessage({
                        type: 'iframe-error',
                        application: '${applicationName}',
                        errorType: 'javascript-error',
                        error: {
                            message: event.message,
                            filename: event.filename,
                            lineno: event.lineno,
                            colno: event.colno,
                            stack: event.error ? event.error.stack : null
                        },
                        url: window.location.href,
                        timestamp: new Date().toISOString()
                    }, '*');
                });

                // Capture unhandled promise rejections
                window.addEventListener('unhandledrejection', function(event) {
                    window.parent.postMessage({
                        type: 'iframe-error',
                        application: '${applicationName}',
                        errorType: 'unhandled-promise-rejection',
                        error: {
                            message: event.reason ? event.reason.toString() : 'Unhandled promise rejection',
                            stack: event.reason && event.reason.stack ? event.reason.stack : null
                        },
                        url: window.location.href,
                        timestamp: new Date().toISOString()
                    }, '*');
                });

                // Capture network errors (basic detection)
                const originalFetch = window.fetch;
                window.fetch = function(...args) {
                    return originalFetch.apply(this, args).catch(error => {
                        window.parent.postMessage({
                            type: 'iframe-error',
                            application: '${applicationName}',
                            errorType: 'api-request-failed',
                            error: {
                                message: error.message,
                                name: error.name,
                                stack: error.stack
                            },
                            url: window.location.href,
                            timestamp: new Date().toISOString()
                        }, '*');
                        throw error;
                    });
                };

                console.log('Iframe error reporting initialized for ${applicationName}');
            })();
        `;
        
        iframeDocument.head.appendChild(script);
    }

    /**
     * Request error reporting from cross-origin iframe using postMessage
     */
    requestErrorReporting(iframe) {
        // Send a message to the iframe requesting it to setup error reporting
        // Note: The iframe application would need to implement this listener
        try {
            iframe.contentWindow.postMessage({
                type: 'setup-error-reporting',
                parentOrigin: window.location.origin
            }, '*');
        } catch (error) {
            console.warn('Could not request error reporting from iframe:', error);
        }
    }

    /**
     * Setup iframe-specific listeners for load and error events
     */
    setupIframeListeners(iframe, applicationName) {
        // Iframe load error
        iframe.addEventListener('error', (event) => {
            this.logError({
                application: applicationName,
                errorType: 'iframe-load-error',
                error: {
                    message: 'Failed to load iframe',
                    name: 'IframeLoadError',
                    stack: null
                },
                severity: 'error',
                url: iframe.src,
                additionalContext: {
                    iframeSrc: iframe.src,
                    parentUrl: window.location.href
                }
            });
        });

        // Iframe security policy violations
        iframe.addEventListener('securitypolicyviolation', (event) => {
            this.logError({
                application: applicationName,
                errorType: 'security-policy-violation',
                error: {
                    message: `CSP violation: ${event.violatedDirective}`,
                    name: 'SecurityPolicyViolation',
                    stack: null
                },
                severity: 'warning',
                additionalContext: {
                    violatedDirective: event.violatedDirective,
                    blockedURI: event.blockedURI,
                    iframeSrc: iframe.src
                }
            });
        });
    }

    /**
     * Setup global error listener for the parent window
     */
    setupGlobalErrorListener() {
        // Capture any iframe-related errors in the parent window
        window.addEventListener('error', (event) => {
            // Check if error is related to an iframe
            if (event.target && event.target.tagName === 'IFRAME') {
                const iframe = event.target;
                const iframeInfo = Array.from(this.iframeErrorListeners.values())
                    .find(info => info.iframe === iframe);

                if (iframeInfo) {
                    this.logError({
                        application: iframeInfo.applicationName,
                        errorType: 'iframe-error',
                        error: {
                            message: event.message || 'Iframe error',
                            filename: event.filename,
                            lineno: event.lineno,
                            colno: event.colno,
                            stack: event.error ? event.error.stack : null
                        },
                        severity: 'error',
                        additionalContext: {
                            iframeSrc: iframe.src
                        }
                    });
                }
            }
        }, true);
    }

    /**
     * Setup postMessage listener for cross-origin iframe errors
     */
    setupPostMessageListener() {
        window.addEventListener('message', (event) => {
            // Validate message format
            if (event.data && event.data.type === 'iframe-error') {
                const errorData = event.data;
                
                // Log the error received from iframe
                this.logError({
                    application: errorData.application || 'unknown-iframe-app',
                    errorType: errorData.errorType || 'iframe-error',
                    error: errorData.error || {
                        message: 'Unknown iframe error',
                        name: 'IframeError',
                        stack: null
                    },
                    severity: errorData.severity || 'error',
                    url: errorData.url || 'unknown',
                    additionalContext: {
                        receivedViaPostMessage: true,
                        iframeOrigin: event.origin,
                        timestamp: errorData.timestamp
                    }
                });
            }
        });
    }

    /**
     * Log error to Firebase according to ERROR_LOGGING_STANDARD.md
     */
    async logError(errorData) {
        try {
            const db = GetDB();
            
            if (!db) {
                console.error('Database connection not available for iframe error logging');
                return;
            }

            // Prepare error document according to standard
            const errorDocument = {
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                application: errorData.application || 'unknown-iframe-app',
                errorType: errorData.errorType || 'iframe-error',
                sessionId: this.sessionId,
                
                error: {
                    message: errorData.error?.message || 'Unknown error',
                    name: errorData.error?.name || 'Unknown',
                    stack: errorData.error?.stack || null,
                    status: errorData.error?.status || null,
                    text: errorData.error?.text || null
                },
                
                // User Environment
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language,
                screenResolution: `${window.screen.width}x${window.screen.height}`,
                viewportSize: `${window.innerWidth}x${window.innerHeight}`,
                
                // Application Context
                url: errorData.url || window.location.href,
                referrer: document.referrer,
                formData: null, // Iframe doesn't have direct access to parent form data
                calculationResults: null, // Would need to be passed from iframe if available
                
                // Additional Metadata
                userEmail: null, // Would need to be passed from parent if available
                severity: errorData.severity || 'error',
                environment: this.getEnvironment(),
                
                // Iframe-specific metadata
                iframeContext: {
                    iframeSrc: errorData.additionalContext?.iframeSrc || 'unknown',
                    parentUrl: window.location.href,
                    crossOrigin: errorData.additionalContext?.receivedViaPostMessage || false,
                    iframeOrigin: errorData.additionalContext?.iframeOrigin || null,
                    ...errorData.additionalContext
                }
            };
            
            await db.collection('errors').add(errorDocument);
            console.log('Iframe error logged to Firebase successfully');
            
        } catch (firestoreError) {
            console.error('Failed to log iframe error to Firebase:', firestoreError);
            // Don't throw - we don't want error logging to break the app
        }
    }

    /**
     * Get current environment
     */
    getEnvironment() {
        const loc = window.location.href;
        if (loc.includes("localhost") || loc.includes("test.deleptual") || loc.includes("deleptual")) {
            return "dev";
        } else if (loc.includes("moniker-virtual")) {
            return "production";
        }
        return "dev";
    }

    /**
     * Cleanup method to remove listeners
     */
    cleanup() {
        this.iframeErrorListeners.clear();
        // Note: In a real implementation, you'd want to track and remove specific listeners
    }
}

// Create singleton instance
const iframeErrorLogger = new IframeErrorLogger();

export default iframeErrorLogger;